import React, { useEffect, useMemo, useRef, useState } from 'react';
import JsonTextArea from './JsonTextArea';
import './FormStyles.css';

const emptyRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  key: '',
  type: 'text',
  value: '',
});

const valueType = value => {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value)) return 'list';
  if (value && typeof value === 'object') return 'json';
  return 'text';
};

const displayValue = (value, type) => {
  if (type === 'list') return value.join('\n');
  if (type === 'json') return JSON.stringify(value, null, 2);
  if (type === 'boolean') return value ? 'true' : 'false';
  return value == null ? '' : String(value);
};

const parseObjectRows = value => {
  const text = String(value || '').trim();
  if (!text) return [];
  const parsed = JSON.parse(text);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error('A JSON object is required');
  }
  return Object.entries(parsed).map(([key, item], index) => {
    const type = valueType(item);
    return {
      id: `${key}-${index}-${Date.now()}`,
      key,
      type,
      value: displayValue(item, type),
    };
  });
};

const rowValue = row => {
  if (row.type === 'number') return Number(row.value);
  if (row.type === 'boolean') return row.value === 'true';
  if (row.type === 'list') {
    return row.value.split(/\r?\n|,/).map(item => item.trim()).filter(Boolean);
  }
  if (row.type === 'json') return JSON.parse(row.value || '{}');
  return row.value;
};

export default function StructuredJsonEditor({
  label,
  name,
  value,
  onChange,
  error,
  disabled = false,
  placeholder,
}) {
  const [mode, setMode] = useState('visual');
  const [rows, setRows] = useState([]);
  const [builderError, setBuilderError] = useState('');
  const lastEmittedValue = useRef(null);

  useEffect(() => {
    if (value === lastEmittedValue.current) {
      lastEmittedValue.current = null;
      return;
    }
    try {
      setRows(parseObjectRows(value));
      setBuilderError('');
    } catch {
      // Invalid raw JSON remains untouched until corrected by the user.
    }
  }, [value]);

  const duplicateKeys = useMemo(() => {
    const keys = rows.map(row => row.key.trim()).filter(Boolean);
    return new Set(keys.filter((key, index) => keys.indexOf(key) !== index));
  }, [rows]);

  const emitObject = nextRows => {
    try {
      const result = {};
      for (const row of nextRows) {
        const key = row.key.trim();
        if (!key) continue;
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          throw new Error(`Duplicate key: ${key}`);
        }
        result[key] = rowValue(row);
      }
      const nextValue = JSON.stringify(result, null, 2);
      lastEmittedValue.current = nextValue;
      setBuilderError('');
      onChange?.({
        target: { name, value: nextValue, type: 'textarea' },
        currentTarget: { name, value: nextValue, type: 'textarea' },
      });
    } catch (nextError) {
      setBuilderError(nextError instanceof SyntaxError
        ? 'Nested JSON value is incomplete or invalid.'
        : nextError.message);
    }
  };

  const updateRow = (index, field, nextValue) => {
    const nextRows = rows.map((row, rowIndex) => rowIndex === index
      ? {
          ...row,
          [field]: nextValue,
          ...(field === 'type'
            ? { value: nextValue === 'boolean' ? 'false' : nextValue === 'json' ? '{}' : '' }
            : {}),
        }
      : row);
    setRows(nextRows);
    emitObject(nextRows);
  };

  const addRow = () => setRows(current => [...current, emptyRow()]);

  const removeRow = index => {
    const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
    setRows(nextRows);
    emitObject(nextRows);
  };

  const selectMode = nextMode => {
    if (nextMode === 'visual') {
      try {
        setRows(parseObjectRows(value));
        setBuilderError('');
      } catch {
        setBuilderError('Correct the JSON before switching to the visual editor.');
        return;
      }
    }
    setMode(nextMode);
  };

  return (
    <div className="structured-json-editor">
      <div className="structured-json-header">
        <div>
          <label className="form-label" htmlFor={`${name}-${mode}`}>{label}</label>
          <small>सोप्या फॉर्ममधून वैशिष्ट्ये जोडा किंवा प्रगत JSON थेट संपादित करा.</small>
        </div>
        <div className="structured-json-mode" role="group" aria-label="Editor mode">
          <button type="button" className={mode === 'visual' ? 'active' : ''} onClick={() => selectMode('visual')}>
            Visual builder
          </button>
          <button type="button" className={mode === 'json' ? 'active' : ''} onClick={() => selectMode('json')}>
            JSON
          </button>
        </div>
      </div>

      {mode === 'json' ? (
        <JsonTextArea
          label=""
          name={name}
          value={value}
          onChange={onChange}
          error={error || builderError}
          disabled={disabled}
          placeholder={placeholder}
          rows={8}
        />
      ) : (
        <>
          <div className="structured-json-rows">
            {rows.map((row, index) => (
              <div className="structured-json-row" key={row.id}>
                <div className="structured-json-field key-field">
                  <label htmlFor={`${name}-key-${row.id}`}>Feature / key</label>
                  <input id={`${name}-key-${row.id}`} type="text" value={row.key} disabled={disabled}
                    placeholder="e.g. doctors" onChange={event => updateRow(index, 'key', event.target.value)}
                    className={duplicateKeys.has(row.key.trim()) ? 'error' : ''} />
                  {duplicateKeys.has(row.key.trim()) && <span className="form-error">This key is already used.</span>}
                </div>
                <div className="structured-json-field type-field">
                  <label htmlFor={`${name}-type-${row.id}`}>Value type</label>
                  <select id={`${name}-type-${row.id}`} value={row.type} disabled={disabled}
                    onChange={event => updateRow(index, 'type', event.target.value)}>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Yes / No</option>
                    <option value="list">List</option>
                    <option value="json">Nested JSON</option>
                  </select>
                </div>
                <div className="structured-json-field value-field">
                  <label htmlFor={`${name}-value-${row.id}`}>Value</label>
                  {row.type === 'boolean' ? (
                    <select id={`${name}-value-${row.id}`} value={row.value} disabled={disabled}
                      onChange={event => updateRow(index, 'value', event.target.value)}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : row.type === 'list' || row.type === 'json' ? (
                    <textarea id={`${name}-value-${row.id}`} value={row.value} disabled={disabled} rows={2}
                      placeholder={row.type === 'list' ? 'One item per line' : '{"key":"value"}'}
                      onChange={event => updateRow(index, 'value', event.target.value)} />
                  ) : (
                    <input id={`${name}-value-${row.id}`} type={row.type === 'number' ? 'number' : 'text'}
                      value={row.value} disabled={disabled}
                      placeholder={row.type === 'number' ? '0' : 'Feature value'}
                      onChange={event => updateRow(index, 'value', event.target.value)} />
                  )}
                </div>
                <button type="button" className="structured-json-remove" disabled={disabled}
                  onClick={() => removeRow(index)} aria-label={`Remove ${row.key || 'feature'}`}>
                  Remove
                </button>
              </div>
            ))}
            {!rows.length && (
              <div className="structured-json-empty">
                <strong>No additional features configured</strong>
                <span>Add a feature such as doctors, beds, services, parking, or accessibility.</span>
              </div>
            )}
          </div>
          <button type="button" className="structured-json-add" disabled={disabled} onClick={addRow}>
            + Add new feature
          </button>
          {(error || builderError) && <div className="form-error structured-json-error">{error || builderError}</div>}
          <small className="form-help">List values can be entered one per line. JSON is generated automatically in the background.</small>
        </>
      )}
    </div>
  );
}
